"use client";
import { useCheckboxStore } from "../checkbox/checkbox-store.js";
import { n as useCheckbox, r as getPrimitiveValue } from "../__chunks/Bz1czXRN.js";
import { useMenuScopedContext } from "./menu-context.js";
import { useMenuItem } from "./menu-item.js";
import { createElement, createHook, forwardRef, memo, useInitialValue } from "@ariakit/react-utils";
import { invariant, shallowEqual } from "@ariakit/utils";
import { useEffect } from "react";
import { useStoreState } from "@ariakit/react-store";
//#region src/menu/menu-item-checkbox.tsx
const TagName = "div";
function getValue(storeValue, value, checked) {
	if (value === void 0) {
		if (Array.isArray(storeValue)) return storeValue;
		return !!checked;
	}
	const primitiveValue = getPrimitiveValue(value);
	if (!Array.isArray(storeValue)) {
		if (checked) return primitiveValue;
		return storeValue === primitiveValue ? false : storeValue;
	}
	if (checked) {
		if (storeValue.includes(primitiveValue)) return storeValue;
		return [...storeValue, primitiveValue];
	}
	return storeValue.filter((v) => v !== primitiveValue);
}
/**
* Returns props to create a `MenuItemCheckbox` component.
* @see https://ariakit.com/components/menu
* @example
* ```jsx
* const store = useMenuStore({ defaultValues: { apple: false } });
* const props = useMenuItemCheckbox({ store, name: "apple" });
* <MenuButton store={store}>Fruits</MenuButton>
* <Menu store={store}>
*   <Role {...props}>Apple</Role>
* </Menu>
* ```
*/
const useMenuItemCheckbox = createHook(function useMenuItemCheckbox({ store, name, value, checked, defaultChecked: defaultCheckedProp, hideOnClick = false, ...props }) {
	const context = useMenuScopedContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "MenuItemCheckbox must be wrapped in a MenuList or Menu component");
	const defaultChecked = useInitialValue(defaultCheckedProp);
	useEffect(() => {
		store?.setValue(name, (prevValue = value !== void 0 ? [] : false) => {
			if (!defaultChecked) return prevValue;
			return getValue(prevValue, value, true);
		});
	}, [
		store,
		name,
		value,
		defaultChecked
	]);
	useEffect(() => {
		if (checked === void 0) return;
		store?.setValue(name, (prevValue) => {
			return getValue(prevValue, value, checked);
		});
	}, [
		store,
		name,
		value,
		checked
	]);
	const checkboxStore = useCheckboxStore({
		value: useStoreState(store, (state) => state.values[name]),
		setValue(internalValue) {
			store?.setValue(name, () => {
				if (checked === void 0) return internalValue;
				const nextValue = getValue(internalValue, value, checked);
				if (!Array.isArray(nextValue)) return nextValue;
				if (!Array.isArray(internalValue)) return nextValue;
				if (shallowEqual(internalValue, nextValue)) return internalValue;
				return nextValue;
			});
		}
	});
	props = {
		role: "menuitemcheckbox",
		...props
	};
	props = useCheckbox({
		store: checkboxStore,
		name,
		value,
		checked,
		...props
	});
	props = useMenuItem({
		store,
		hideOnClick,
		...props
	});
	return props;
});
/**
* Renders a [`menuitemcheckbox`](https://w3c.github.io/aria/#menuitemcheckbox)
* element within a [`Menu`](https://ariakit.com/reference/menu) component. The
* [`name`](https://ariakit.com/reference/menu-item-checkbox#name) prop must be
* provided to identify the field in the
* [`values`](https://ariakit.com/reference/menu-provider#values) state.
*
* A [`MenuItemCheck`](https://ariakit.com/reference/menu-item-check) can be
* used to render a checkmark inside this component.
* @see https://ariakit.com/components/menu
* @example
* The [`name`](https://ariakit.com/reference/menu-item-checkbox#name) prop can
* refer to a single value in the state:
* ```jsx {4-7}
* <MenuProvider defaultValues={{ warnBeforeQuitting: true }}>
*   <MenuButton>Chrome</MenuButton>
*   <Menu>
*     <MenuItemCheckbox name="warnBeforeQuitting">
*       <MenuItemCheck />
*       Warn Before Quitting
*     </MenuItemCheckbox>
*   </Menu>
* </MenuProvider>
* ```
* @example
* Or it can refer to an array of values, in which case the
* [`value`](https://ariakit.com/reference/menu-item-checkbox#value) prop must
* be provided:
* ```jsx {4-9}
* <MenuProvider defaultValues={{ watching: ["issues"] }}>
*   <MenuButton>Watch</MenuButton>
*   <Menu>
*     <MenuItemCheckbox name="watching" value="issues">
*       Issues
*     </MenuItemCheckbox>
*     <MenuItemCheckbox name="watching" value="pull-requests">
*       Pull Requests
*     </MenuItemCheckbox>
*   </Menu>
* </MenuProvider>
* ```
*/
const MenuItemCheckbox = memo(forwardRef(function MenuItemCheckbox(props) {
	return createElement(TagName, useMenuItemCheckbox(props));
}));
//#endregion
export { MenuItemCheckbox, useMenuItemCheckbox };

//# sourceMappingURL=menu-item-checkbox.js.map